import { useEffect, useState, useCallback } from "react";
import { Tooltip } from "bootstrap";
import { callOperation } from "../../utils/apiHelper.js";

/**
 * Custom hook to initialize Bootstrap tooltips
 * Used by both VepForm and FiltersForm
 */
export const useTooltips = () => {
  useEffect(() => {
    const tooltipTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="tooltip"]')
    );
    tooltipTriggerList.forEach(
      (tooltipTriggerEl) =>
        new Tooltip(tooltipTriggerEl, {
          placement: "right",
          boundary: "viewport",
          trigger: "hover",
          delay: { show: 300, hide: 100 },
          html: true,
          sanitize: false,
        })
    );

    // Cleanup function to dispose tooltips when component unmounts
    return () => {
      tooltipTriggerList.forEach((tooltipTriggerEl) => {
        const tooltipInstance = Tooltip.getInstance(tooltipTriggerEl);
        if (tooltipInstance) {
          tooltipInstance.dispose();
        }
      });
    };
  }, []);
};

/**
 * Generic input change handler factory
 * Creates a handler function for form input changes
 */
export const createInputChangeHandler = (
  formData,
  setFormData,
  onDataChange
) => {
  return (event) => {
    const { name, id, value, type, checked } = event.target;
    const fieldName = name || id; // Support both name and id attributes

    const updatedData = {
      ...formData,
      [fieldName]: type === "checkbox" ? checked : value,
    };

    // Remove empty values
    if (value === "") {
      delete updatedData[fieldName];
    }

    setFormData(updatedData);
    if (onDataChange) {
      onDataChange(updatedData);
    }
  };
};

/**
 * Custom hook for managing collapsible panels state
 * Supports both simple boolean state and nested object state patterns
 *
 * @param {Array} panels - Array of panel configuration objects
 * @param {Object} options - Configuration options
 * @param {string} options.mode - 'simple' for {panelId: boolean} or 'nested' for {panelId: {collapsed: boolean}}
 * @param {string} options.idField - Field name to use as panel identifier (default: 'title' for simple, 'id' for nested)
 * @param {string} options.collapsedField - Field name for initial collapsed state (default: 'collapsed')
 * @param {Function} options.initialStateMapper - Custom function to map panel to initial state
 * @returns {Object} { collapsedState, toggleCollapse, isCollapsed }
 */
export const useCollapsiblePanels = (panels = [], options = {}) => {
  const {
    mode = "simple",
    idField = mode === "simple" ? "title" : "id",
    collapsedField = "collapsed",
    initialStateMapper,
  } = options;

  // Initialize collapsible state based on mode
  const [collapsedState, setCollapsedState] = useState(() => {
    if (initialStateMapper) {
      return initialStateMapper(panels);
    }

    return panels.reduce((acc, panel) => {
      const panelId = panel[idField];
      const initialCollapsed = panel[collapsedField] || false;

      if (mode === "simple") {
        acc[panelId] = initialCollapsed;
      } else if (mode === "nested") {
        acc[panelId] = {
          ...panel,
          collapsed: initialCollapsed,
        };
      }

      return acc;
    }, {});
  });

  // Toggle collapse function
  const toggleCollapse = (panelId) => {
    setCollapsedState((prevState) => {
      if (mode === "simple") {
        return {
          ...prevState,
          [panelId]: !prevState[panelId],
        };
      } else if (mode === "nested") {
        return {
          ...prevState,
          [panelId]: {
            ...prevState[panelId],
            collapsed: !prevState[panelId]?.collapsed,
          },
        };
      }
      return prevState;
    });
  };

  // Utility function to check if a panel is collapsed
  const isCollapsed = (panelId) => {
    if (mode === "simple") {
      return collapsedState[panelId] || false;
    } else if (mode === "nested") {
      return collapsedState[panelId]?.collapsed || false;
    }
    return false;
  };

  return {
    collapsedState,
    setCollapsedState,
    toggleCollapse,
    isCollapsed,
  };
};

/**
 * Custom hook for managing progress polling operations
 * Handles starting processes, polling for progress, and managing loading states
 *
 * @param {string} processType - Type of process to monitor (e.g., 'VEP')
 * @param {string} token - Authentication token
 * @param {number} pollInterval - Polling interval in milliseconds (default: 2000)
 * @returns {Object} { isRunning, progress, maxProgress, startProcess, checkProgress }
 */
export const useProgressPolling = (processType, token, pollInterval = 2000) => {
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [maxProgress, setMaxProgress] = useState(100);
  const [pollingInterval, setPollingInterval] = useState(null);

  // Check progress from server
  const checkProgress = useCallback(async () => {
    if (!processType || !token) {
      return { progress: 0, maxProgress: 100, running: false };
    }

    try {
      const response = await callOperation("get_progress", { file_name: processType }, token);

      return {
        progress: response.data.progress || 0,
        maxProgress: response.data.maxProgress || 100,
        running: response.data.running || false,
      };
    } catch (error) {
      console.error("Error checking progress:", error);
      return { progress: 0, maxProgress: 100, running: false };
    }
  }, [processType, token]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  }, [pollingInterval]);

  // Start polling for progress
  const startPolling = useCallback(() => {
    // Only start polling if not already polling
    if (pollingInterval) {
      return;
    }

    const progressInterval = setInterval(async () => {
      const {
        progress: currentProgress,
        maxProgress: currentMaxProgress,
        running,
      } = await checkProgress();

      setProgress(currentProgress);
      setMaxProgress(currentMaxProgress);
      setIsRunning(running);

      if (!running) {
        clearInterval(progressInterval);
        setPollingInterval(null);
        setIsRunning(false);
      }
    }, pollInterval);

    setPollingInterval(progressInterval);
  }, [checkProgress, pollInterval, pollingInterval]);

  // Start a new process
  const startProcess = useCallback(
    async (processData = {}) => {
      if (!processType || !token) {
        throw new Error("Process type and token are required");
      }

      // Check if already running
      const { running } = await checkProgress();

      if (running) {
        setIsRunning(true);
        startPolling();
      } else {
        setIsRunning(true);

        try {
          const response = await callOperation("run_stats", { statsType: processType, ...processData }, token);

          startPolling();
          return response;
        } catch (error) {
          console.error(`Error starting ${processType} process:`, error);
          setIsRunning(false);
          throw error;
        }
      }
    },
    [processType, token, checkProgress, startPolling]
  );

  // Check progress on mount
  useEffect(() => {
    const checkProgressOnMount = async () => {
      const {
        progress: currentProgress,
        maxProgress: currentMaxProgress,
        running,
      } = await checkProgress();

      setProgress(currentProgress);
      setMaxProgress(currentMaxProgress);

      if (running) {
        setIsRunning(true);
        startPolling();
      }
    };

    if (processType && token) {
      checkProgressOnMount();
    }
  }, [processType, token, checkProgress, startPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    isRunning,
    progress,
    maxProgress,
    startProcess,
    checkProgress,
  };
};
